// Builds welcome examples from the left menu tree (already rights-filtered), so nothing is named
// the reader can't see; falls back to neutral examples until the tree is loaded.

/** What the welcome's "See what I can do" link sends: the assistant answers it from a fixed template. */
export const CAPABILITIES_QUESTION = "What can you do?";
export const HELP_URL = "https://xormon.com/AI.php";

/** The six kinds of question the welcome shows, one example each, in the order of NEUTRAL_STARTERS. */
export type StarterShape = "count" | "rank" | "list" | "trend" | "status" | "relation";

export interface StarterQuestion {
  key: StarterShape;
  label: string;
}

// Subset of the app's MenuNode this file needs; tests build plain objects of the same shape.
export interface MenuNodeLike {
  key?: unknown;
  url?: string;
  title?: unknown;
  titleText?: string;
  hwType?: string | string[];
  subsystem?: string;
  isLeaf?: boolean;
  divider?: boolean;
  notActive?: boolean;
  isGroupFolder?: boolean;
  children?: MenuNodeLike[];
}

export interface EstatePlatform {
  hwType: string;
  label: string;
}

export interface EstateDevice {
  name: string;
  hwType?: string;
}

/** One monitored area (a class such as storage or virtualization) as the menu tree shows it. */
export interface EstateArea {
  key: string;
  platforms: EstatePlatform[];
  devices: EstateDevice[];
  fabrics: string[];
}

export interface Estate {
  areas: EstateArea[];
  storageDevices: EstateDevice[];
}

/** Questions that name nothing: shown while the tree loads, and for a kind the installation has nothing for. */
export const NEUTRAL_STARTERS: readonly StarterQuestion[] = [
  { key: "count", label: "How many devices do I monitor?" },
  { key: "rank", label: "Which devices used the most CPU yesterday?" },
  { key: "list", label: "Which platforms do I monitor?" },
  { key: "trend", label: "How did CPU usage change over the last 7 days?" },
  { key: "status", label: "Which alerts are firing right now?" },
  { key: "relation", label: "Which topologies can you show me?" },
];

export const STARTER_SHAPES: readonly StarterShape[] = NEUTRAL_STARTERS.map((one) => one.key);

// Order = priority for which one the examples name; a platform not listed still counts, just never as VMs.
const VM_PLATFORMS = ["vmware", "nutanix", "proxmox", "ovirt", "xenserver", "oraclevm", "fusioncompute"];
/** IBM Power's virtual servers are LPARs. */
const LPAR_PLATFORMS = ["power"];

// Not every storage platform records volumes or IOPS (backup/object/file stores don't); only listed
// platforms get named in those questions.
const VOLUME_PLATFORMS = new Set([
  "3par", "ceph", "compellent", "datacore", "ddnintelliflash", "dothill", "ds8k", "eseries", "eternus", "hcp",
  "hnas", "infinibox", "infortrend", "metronode", "netapp", "nimble", "oceanstor", "oceanstorpacific", "oraclezfs",
  "powerflex", "powerstore", "pure", "qnap", "scalityring", "swiz", "synology", "unity", "vmax", "vplex", "vspg",
  "xiv", "xtremio",
]);
const IOPS_PLATFORMS = new Set([
  "3par", "ceph", "cohesity", "compellent", "datacore", "datadomain", "ddnintelliflash", "dothill", "ds8k", "ecs",
  "eseries", "eternus", "gpfs", "hnas", "infinibox", "metronode", "netapp", "nimble", "oceanstor", "oceanstornas",
  "oceanstorpacific", "oceanstorpacificnas", "oraclezfs", "powerflex", "powerstore", "pure", "pureblade", "qnap",
  "qumulo", "rubrik", "swiz", "swizflash", "synology", "unity", "vmax", "vplex", "vspg", "xiv", "xtremio",
]);

function textOf(node: MenuNodeLike): string | undefined {
  const text = typeof node.titleText === "string" ? node.titleText : typeof node.title === "string" ? node.title : undefined;
  const trimmed = text?.trim();
  return trimmed ? trimmed : undefined;
}

/** A class at the top of the tree: its node points at the class page. Dashboards and custom groups do not. */
function isAreaNode(node: MenuNodeLike): boolean {
  return typeof node.key === "string" && node.url === "/" + node.key && !node.notActive;
}

function deviceOf(node: MenuNodeLike): EstateDevice | undefined {
  if (node.subsystem !== "device" || node.divider) {
    return undefined;
  }
  const name = textOf(node);
  if (!name) {
    return undefined;
  }
  return typeof node.hwType === "string" ? { name, hwType: node.hwType } : { name };
}

function areaOf(node: MenuNodeLike): EstateArea {
  const platforms: EstatePlatform[] = [];
  const devices: EstateDevice[] = [];
  const fabrics: string[] = [];
  for (const child of node.children ?? []) {
    if (child.divider || child.isGroupFolder) {
      continue;
    }
    const device = deviceOf(child);
    if (device) {
      devices.push(device);
      continue;
    }
    const label = textOf(child);
    if (!label) {
      continue;
    }
    if (child.subsystem === "fabric") {
      fabrics.push(label);
    } else if (!child.subsystem && !child.isLeaf && typeof child.hwType === "string" && child.hwType) {
      platforms.push({ hwType: child.hwType.toLowerCase(), label });
    }
  }
  return { key: node.key as string, platforms, devices, fabrics };
}

// Storage devices come from a separate list, since the storage area itself only carries platforms.
export function estateFromMenu(topMenu: readonly MenuNodeLike[] | undefined, storageDevices?: readonly MenuNodeLike[]): Estate | undefined {
  if (!topMenu?.length) {
    return undefined;
  }
  const areas = topMenu.filter(isAreaNode).map(areaOf)
    .filter((area) => area.platforms.length || area.devices.length || area.fabrics.length);
  const stored = (storageDevices ?? []).map(deviceOf).filter((one): one is EstateDevice => !!one);
  return { areas, storageDevices: stored };
}

interface Facts {
  vmPlatform?: string;
  lparPlatform?: string;
  storage: boolean;
  /** Some storage platform here records IOPS for its devices. */
  storageIops: boolean;
  san: boolean;
  lan: boolean;
  tape: boolean;
  storageNames: string[];
  /** Storage devices whose platform has volumes. */
  volumeNames: string[];
  /** Storage devices whose platform records IOPS. */
  iopsNames: string[];
  sanNames: string[];
  lanNames: string[];
  tapeNames: string[];
  fabrics: string[];
}

function uniqueNames(devices: readonly EstateDevice[]): string[] {
  return [...new Set(devices.map((one) => one.name))];
}

function factsOf(estate: Estate): Facts {
  const area = (key: string) => estate.areas.find((one) => one.key === key);
  const platforms = estate.areas.flatMap((one) => one.platforms);
  const firstOf = (order: readonly string[]) => {
    for (const hwType of order) {
      const found = platforms.find((one) => one.hwType === hwType);
      if (found) {
        return found.label;
      }
    }
    return undefined;
  };
  const storageArea = area("storage");
  const san = area("san");
  const lan = area("lan");
  const tape = area("tape");
  const storageDevices = estate.storageDevices.length ? estate.storageDevices : storageArea?.devices ?? [];
  const onPlatforms = (platformSet: Set<string>) => uniqueNames(storageDevices.filter((one) => !!one.hwType && platformSet.has(one.hwType.toLowerCase())));
  const iopsNames = onPlatforms(IOPS_PLATFORMS);
  return {
    vmPlatform: firstOf(VM_PLATFORMS),
    lparPlatform: firstOf(LPAR_PLATFORMS),
    storage: !!storageArea || storageDevices.length > 0,
    storageIops: iopsNames.length > 0 || !!storageArea?.platforms.some((one) => IOPS_PLATFORMS.has(one.hwType)),
    san: !!san,
    lan: !!lan,
    tape: !!tape,
    storageNames: uniqueNames(storageDevices),
    volumeNames: onPlatforms(VOLUME_PLATFORMS),
    iopsNames,
    sanNames: uniqueNames(san?.devices ?? []),
    lanNames: uniqueNames(lan?.devices ?? []),
    tapeNames: uniqueNames(tape?.devices ?? []),
    fabrics: [...new Set(san?.fabrics ?? [])],
  };
}

/** A sentence that needs one name, and the names it may take. */
interface Named {
  names: string[];
  say: (name: string) => string;
}

// Hands out fresh names first so examples cover different devices; repeats only if `repeat` allows it.
class NamePicker {
  private readonly used = new Set<string>();

  pick(options: readonly Named[], repeat = true): string | undefined {
    for (const option of options) {
      const fresh = option.names.find((name) => !this.used.has(name));
      if (fresh) {
        this.used.add(fresh);
        return option.say(fresh);
      }
    }
    if (!repeat) {
      return undefined;
    }
    const any = options.find((option) => option.names.length);
    return any ? any.say(any.names[0]) : undefined;
  }
}

/** The question of the first row whose fact the installation has; the rows are in order of preference. */
function firstMatch(rows: readonly (readonly [unknown, string])[]): string | undefined {
  return rows.find(([fact]) => fact)?.[1];
}

function countQuestion(facts: Facts): string | undefined {
  return firstMatch([
    [facts.san, "How many SAN switches are there?"],
    [facts.vmPlatform, `How many ${facts.vmPlatform} VMs are there?`],
    [facts.storage, "How many storage devices are there?"],
    [facts.tape, "How many tape libraries are there?"],
    [facts.lan, "How many LAN switches are there?"],
  ]);
}

function rankQuestion(facts: Facts): string | undefined {
  return firstMatch([
    [facts.vmPlatform, `Top 5 ${facts.vmPlatform} VMs by CPU over the last day`],
    [facts.lparPlatform, `Top 5 ${facts.lparPlatform} LPARs by CPU over the last day`],
    [facts.storageIops, "Top 5 storage devices by IOPS over the last day"],
  ]);
}

function listQuestion(facts: Facts, names: NamePicker): string | undefined {
  return names.pick([{ names: facts.volumeNames, say: (name) => `Show volumes under ${name}` }]) ?? firstMatch([
    [facts.tape, "List all tape libraries I have configured"],
    [facts.storage, "Give me a list of storage devices with names, IP addresses and vendor names."],
    [facts.vmPlatform, `List all ${facts.vmPlatform} VMs`],
    [facts.san, "List all SAN switches"],
  ]);
}

function trendQuestion(facts: Facts, names: NamePicker): string | undefined {
  // Every SAN switch platform NG knows records CPU usage for the switch.
  return names.pick([
    { names: facts.iopsNames, say: (name) => `Show the IOPS of ${name} over the last 7 days` },
    { names: facts.sanNames, say: (name) => `Show the CPU usage of ${name} over the last 7 days` },
  ]);
}

// Names a device nothing else has named yet; with none left, falls back to the alerts question
// rather than repeating a name.
function statusQuestion(facts: Facts, names: NamePicker): string | undefined {
  const health = (name: string) => `What is the health of ${name}?`;
  return names.pick([
    { names: facts.sanNames, say: health },
    { names: facts.storageNames, say: health },
    { names: facts.lanNames, say: health },
    { names: facts.tapeNames, say: health },
  ], false);
}

function relationQuestion(facts: Facts, names: NamePicker): string | undefined {
  const connected = (name: string) => `What is connected to ${name}?`;
  return names.pick([
    { names: facts.storageNames, say: connected },
    { names: facts.sanNames, say: connected },
    { names: facts.fabrics, say: (name) => `Which switches are in fabric ${name}?` },
    { names: facts.lanNames, say: connected },
  ]);
}

// Falls back to the neutral example per kind, so there are always six and none of them make anything up.
export function starterQuestions(estate: Estate | undefined): StarterQuestion[] {
  if (!estate) {
    return NEUTRAL_STARTERS.map((one) => ({ ...one }));
  }
  const facts = factsOf(estate);
  const names = new NamePicker();
  // The order matters: the named kinds take their names in the order they are shown.
  const built: Record<StarterShape, string | undefined> = {
    count: countQuestion(facts),
    rank: rankQuestion(facts),
    list: listQuestion(facts, names),
    trend: trendQuestion(facts, names),
    status: statusQuestion(facts, names),
    relation: relationQuestion(facts, names),
  };
  return STARTER_SHAPES.map((key) => ({ key, label: built[key] ?? neutralLabel(key) }));
}

/** True when at least one example came from the installation rather than from the neutral set. */
export function builtFromEstate(questions: readonly StarterQuestion[]): boolean {
  return questions.some((one) => one.label !== neutralLabel(one.key));
}

function neutralLabel(key: StarterShape): string {
  return NEUTRAL_STARTERS.find((one) => one.key === key)?.label ?? "";
}
